/**
 * Workflow Recipes API Service (admin-portal -- FI-tenant scope)
 *
 * The FI admin's HALF of the recipe surface. Contract:
 * schema-registry/workflow-recipes.openapi.yaml. Every shape here is imported
 * from the generated leaf; none is re-declared.
 *
 * SCOPE. This file lives on admin.aaim.com : 9300 (adminPortal, FI-tenant).
 * Ruling 3 of 2026-08-13, reaffirmed as ruling D3 on 2026-08-17, splits the
 * recipe surface across two personas on two hosts:
 *
 *   Aaim operator (platform.aaim.com : 9303) -- authoring a recipe, publishing
 *     it, retiring it, and deciding WHICH recipes a given tenant may choose
 *     from (the allowlist). All of it behind requirePlatformAdmin.
 *   FI admin (here) -- SELECTING one ordering out of that allowlisted subset,
 *     behind settings:update, plus the reads that make the choice legible.
 *
 * So this client deliberately exposes FOUR operations and stops:
 *
 *   getAllowlist  GET  /tenants/{tenantId}/allowlist          settings:read
 *   getBinding    GET  /tenants/{tenantId}/binding            settings:read
 *   getFamily     GET  /{recipeId}                            settings:read
 *   putBinding    PUT  /tenants/{tenantId}/binding            settings:update
 *
 * There is NO putAllowlist here, and adding one would be the defect this split
 * exists to prevent -- the server would 403 an FI admin at requirePlatformAdmin
 * anyway, so a client method for it could only ever render a dead control.
 * getFamily is a read, not an edit: it is how the page shows the gate sequence
 * behind each allowlisted option so the FI can see what it is choosing.
 *
 * TENANT. Every path above takes a tenantId, and the server checks it against
 * resolveEffectiveTenantId(req) -- a caller reaching for another tenant gets a
 * 404 rather than a 403, so cross-tenant existence does not leak. The SPA's
 * tenantId comes from the authenticated session claim in the auth store, never
 * from a URL segment the user can edit.
 *
 * baseUrl is '' rather than '/api/v1' because this spec declares its paths
 * fully qualified (`/api/v1/workflow-recipes/...`) -- the generated path keys
 * already carry the prefix, so prepending it again would produce
 * /api/v1/api/v1/... The sibling collateral-policy service uses '/api/v1'
 * because its spec's keys are unprefixed. Both are correct for their spec.
 * Both land on platform-admin-api (9302) via the catch-all `/api` proxy in
 * vite.config.ts and nginx.conf, which forwards with no path rewrite.
 */

import createClient from 'openapi-fetch';
import type {
  paths,
  components,
} from '@aaimplatform/institutional-types/generated/workflow-recipes';
import { authMiddleware } from '../client';

export type WorkflowRecipe = components['schemas']['WorkflowRecipe'];
export type WorkflowRecipeFamily = components['schemas']['WorkflowRecipeFamily'];
export type RecipeStep = components['schemas']['RecipeStep'];
export type RecipeGate = components['schemas']['RecipeGate'];
export type RecipeActor = components['schemas']['RecipeActor'];
export type RecipeStatus = components['schemas']['RecipeStatus'];
export type RecipeValidationError = components['schemas']['RecipeValidationError'];
export type TenantRecipeAllowlist = components['schemas']['TenantRecipeAllowlist'];
export type TenantRecipeBinding = components['schemas']['TenantRecipeBinding'];

function createWorkflowRecipesClient() {
  const client = createClient<paths>({ baseUrl: '', credentials: 'include' });
  client.use(authMiddleware);
  return client;
}

const client = createWorkflowRecipesClient();

/**
 * A 422 from the binding write carries `violations` -- the server's V6 and
 * ALLOWLIST_VIOLATION rules. They are surfaced verbatim rather than
 * re-implemented in the SPA: the server is the authority on what is bindable,
 * and a second copy of those rules here would be a parallel contract that
 * drifts from the one that actually gates the write (the near-miss class
 * .claude/rules/canon-fit-escalation.md names).
 */
// adhoc-type-skip: a discriminated result over the generated violation type; declares no new domain shape
export type RecipeMutationResult<T> =
  | { ok: true; data: T }
  | { ok: false; violations: RecipeValidationError[]; message: string };

function toFailure(
  error: unknown,
  fallback: string,
): { ok: false; violations: RecipeValidationError[]; message: string } {
  const e = error as { message?: string; violations?: RecipeValidationError[] } | undefined;
  return {
    ok: false,
    violations: e?.violations ?? [],
    message: e?.message ?? fallback,
  };
}

export const workflowRecipesApi = {
  /**
   * The recipe families this tenant may bind to. An EMPTY `recipeIds` is a
   * legitimate steady state, not a failure: the Aaim operator has made no
   * alternative ordering available, and the tenant runs the platform default.
   */
  getAllowlist: async (tenantId: string): Promise<TenantRecipeAllowlist | null> => {
    const { data, error, response } = await client.GET(
      '/api/v1/workflow-recipes/tenants/{tenantId}/allowlist',
      { params: { path: { tenantId } } },
    );
    if (response.status === 404) return null;
    if (error) throw new Error('Failed to load the orderings available to this institution');
    return data ?? null;
  },

  /**
   * The ordering this tenant has selected. 404 means no selection has been
   * made -- the tenant runs the Aaim platform default -- and resolves to null
   * rather than an error, because "nothing selected" is the majority state.
   */
  getBinding: async (tenantId: string): Promise<TenantRecipeBinding | null> => {
    const { data, error, response } = await client.GET(
      '/api/v1/workflow-recipes/tenants/{tenantId}/binding',
      { params: { path: { tenantId } } },
    );
    if (response.status === 404) return null;
    if (error) throw new Error('Failed to load the selected ordering');
    return data ?? null;
  },

  /**
   * One recipe family with all of its versions and which one is published.
   * The page reads the published version's steps to render the gate sequence
   * behind each option -- a recipe id alone tells an FI admin nothing about
   * what selecting it would do.
   */
  getFamily: async (recipeId: string): Promise<WorkflowRecipeFamily | null> => {
    const { data, error, response } = await client.GET('/api/v1/workflow-recipes/{recipeId}', {
      params: { path: { recipeId } },
    });
    if (response.status === 404) return null;
    if (error) throw new Error('Failed to load the ordering');
    return data ?? null;
  },

  /**
   * Select an ordering for this tenant. A binding names a specific version,
   * never "latest": an in-flight pledge is pinned at creation and no ordering
   * change is compatible with one already running.
   */
  putBinding: async (
    tenantId: string,
    recipeId: string,
    recipeVersion: number,
  ): Promise<RecipeMutationResult<TenantRecipeBinding>> => {
    const { data, error } = await client.PUT(
      '/api/v1/workflow-recipes/tenants/{tenantId}/binding',
      { params: { path: { tenantId } }, body: { recipeId, recipeVersion } },
    );
    if (error) return toFailure(error, 'The ordering could not be selected');
    return { ok: true, data: data as TenantRecipeBinding };
  },
};
